// New comprehensive feed generation engine following detailed specification
import type { DashaContext, TransitContext, FeedItem, LifeArea, HouseNum, PlanetName, DashaLevel } from './types';
import { HOUSE_TO_LIFE, PLANET_NATURE } from './types';
import { TEMPLATES, SHORT_TEMPLATES } from './templates';
import { RULES } from './rules';
import { loadClassicalYogasAsRules, getLastLoadedYogaRules } from './classicalYogasIntegration';
import { applyBhavaSandhiToStrength } from '../../utils/bhavaSandhi';

/**
 * Level weights per specification:
 * MD: 0 (theme only, no event firing)
 * AD: 0 (theme only, no event firing)
 * PD: 0.6 (event enabler)
 * SD: 1.0 (event trigger, highest weight)
 */
export function getLevelWeight(level: 'MD'|'AD'|'PD'|'SD'): number {
  switch (level) {
    case 'MD': return 0;
    case 'AD': return 0;
    case 'PD': return 0.6;
    case 'SD': return 1.0;
  }
}

/**
 * Gate A: Activation
 * Get active houses for a dasha level (rules ∪ occupies ∪ aspects)
 */
function getActiveHouses(level: DashaLevel): HouseNum[] {
  const houses = new Set<HouseNum>();
  level.rules.forEach(h => houses.add(h));
  houses.add(level.occupies);
  (level.aspects || []).forEach(h => houses.add(h));
  return Array.from(houses);
}

/**
 * Map houses to life areas using HOUSE_TO_LIFE
 */
function housesToLifeAreas(houses: HouseNum[]): LifeArea[] {
  const areas = new Set<LifeArea>();
  houses.forEach(h => {
    (HOUSE_TO_LIFE[h] || []).forEach(a => areas.add(a));
  });
  return Array.from(areas);
}

/**
 * Gate B: Polarity - Functional nature weight
 * This requires functional classification from the app
 * For now, we'll use a placeholder that should be passed in
 */
export type FunctionalClassification = Record<PlanetName, 'benefic'|'malefic'|'neutral'|'yoga_karaka'>;

/**
 * Context-aware interpretation of planet's effect on house condition
 * - Benefics: Improve good houses, cushion bad houses
 * - Malefics: Amplify good houses (through effort), worsen bad houses
 * - Yoga karaka: Strong improvement regardless
 * 
 * This avoids the "double negative = positive" paradox where a malefic
 * in an afflicted house would incorrectly produce a positive score.
 */
function computePlanetHouseInteraction(
  houseScore: number,
  planet: PlanetName,
  functional: FunctionalClassification
): number {
  const classification = functional[planet] || 'neutral';
  const isHouseAfflicted = houseScore < 0;
  const absHouseScore = Math.abs(houseScore);
  
  let result = houseScore;
  
  // Yoga karaka: Always beneficial, strong positive influence
  if (classification === 'yoga_karaka') {
    result = isHouseAfflicted ? absHouseScore * 0.7 : absHouseScore * 1.3;
  }
  // Functional benefic: Helps good, cushions bad
  else if (classification === 'benefic') {
    result = isHouseAfflicted ? absHouseScore * 0.6 : absHouseScore * 1.1;
  }
  // Functional malefic: Worsens bad, may produce results through struggle in good
  else if (classification === 'malefic') {
    result = isHouseAfflicted ? -absHouseScore * 1.2 : absHouseScore * 0.8;
  }
  // Neutral: Pass through as-is (already set above)
  
  return result;
}

/**
 * Gate C: Magnitude - Base impact calculation with context-aware planet-house interaction
 */
function computeBaseImpact(
  houses: HouseNum[],
  level: 'PD'|'SD',
  planet: PlanetName,
  houseScores: Record<number, number>,
  functional: FunctionalClassification,
  natalPlacements?: Partial<Record<PlanetName, { house: HouseNum; offsetDeg?: number }>>
): number {
  if (houses.length === 0) return 0;
  
  // Normalize house scores: -100..+100 → -1..+1
  const normalizedScores = houses.map(h => {
    const rawScore = houseScores[h] ?? 0;
    const normalizedScore = Math.max(-1, Math.min(1, rawScore / 100));
    
    // Apply context-aware planet-house interaction
    return computePlanetHouseInteraction(normalizedScore, planet, functional);
  });
  
  const meanScore = normalizedScores.reduce((a, b) => a + b, 0) / normalizedScores.length;
  const levelWeight = getLevelWeight(level);
  
  let baseImpact = meanScore * levelWeight * 100;
  
  // ===== BHAVA SANDHI (CUSP PROXIMITY) ADJUSTMENT =====
  // Apply degree-based multiplier if feeds-only mode is enabled
  const bhavaSandhiMode = typeof window !== 'undefined' ? localStorage.getItem('bhavaSandhiMode') : null;
  if (bhavaSandhiMode === 'feeds-only' && natalPlacements) {
    const placement = natalPlacements[planet];
    if (placement?.offsetDeg !== undefined && placement.house) {
      // Only apply to occupying planets (has house placement)
      // Check if any of the active houses match the planet's house
      if (houses.includes(placement.house)) {
        baseImpact = applyBhavaSandhiToStrength(baseImpact, placement.offsetDeg, true);
      }
    }
  }
  
  return baseImpact;
}

// Dignity status type used for natal and transit dignity maps
export type DignityStatus = 'exalted' | 'moolatrikona' | 'own' | 'friend' | 'neutral' | 'enemy' | 'debilitated';

/**
 * Returns a multiplier for the planet based on its dignity.
 * Conservative values that modulate but never dominate.
 * Natal (dasha lord): exalted=1.25, moolatrikona/own=1.10, friend=1.05, enemy=0.90, debilitated=0.75
 * Transit planets: exalted=1.20, moolatrikona/own=1.10, friend=1.05, enemy=0.92, debilitated=0.80
 */
export function dignityMultiplier(status: DignityStatus | undefined, scope: 'natal' | 'transit'): number {
  if (!status) return 1.0;
  if (scope === 'natal') {
    switch (status) {
      case 'exalted':      return 1.25;
      case 'moolatrikona': return 1.10;
      case 'own':          return 1.10;
      case 'friend':       return 1.05;
      case 'enemy':        return 0.90;
      case 'debilitated':  return 0.75;
      default:             return 1.00;
    }
  } else {
    switch (status) {
      case 'exalted':      return 1.20;
      case 'moolatrikona': return 1.10;
      case 'own':          return 1.10;
      case 'friend':       return 1.05;
      case 'enemy':        return 0.92;
      case 'debilitated':  return 0.80;
      default:             return 1.00;
    }
  }
}

/**
 * Gate D: Probability - Transit reinforcement with detailed bonuses/penalties
 */
interface TransitReinforceArgs {
  targetHouses: HouseNum[];
  transits: TransitContext;
  lifeArea: LifeArea;
  sdPlanet?: PlanetName;
  pdPlanet?: PlanetName;
  sdOccupiedHouse?: HouseNum;
  pdOccupiedHouse?: HouseNum;
  sdRuledHouses?: HouseNum[];
  pdRuledHouses?: HouseNum[];
  isCombust?: boolean;
  transitDignityMap?: Partial<Record<PlanetName, DignityStatus>>;
}

function computeTransitReinforcement(args: TransitReinforceArgs): number {
  const {
    targetHouses,
    transits,
    lifeArea,
    sdPlanet,
    pdPlanet,
    sdOccupiedHouse,
    pdOccupiedHouse,
    sdRuledHouses = [],
    pdRuledHouses = [],
    isCombust = false,
    transitDignityMap = {}
  } = args;
  
  let P = 0;
  const targetSet = new Set(targetHouses);
  
  // Helper: check if house is in trine to any target
  const isTrineToAny = (h: HouseNum): boolean => {
    return targetHouses.some(t => {
      const tri1 = ((t - 1 + 4) % 12) + 1 as HouseNum; // +5th
      const tri2 = ((t - 1 + 8) % 12) + 1 as HouseNum; // +9th
      return h === t || h === tri1 || h === tri2;
    });
  };
  
  // Helper: get Saturn aspects (3, 7, 10)
  const getSaturnAspects = (h: HouseNum): HouseNum[] => {
    return [
      ((h - 1 + 2) % 12) + 1 as HouseNum, // 3rd
      ((h - 1 + 6) % 12) + 1 as HouseNum, // 7th
      ((h - 1 + 9) % 12) + 1 as HouseNum  // 10th
    ];
  };
  
  // Track what we've seen to avoid double-counting
  let jupiterTrineBonus = false;
  let saturnPenalty = false;
  let marRahKetRisk = false;
  let beneficWealth = false;
  let transitActivation = 0;
  let sdRuledTransit = 0;
  
  for (const pos of transits.positions) {
    const { planet, house, aspects = [] } = pos;
    const allHits = new Set([house, ...aspects] as HouseNum[]);
    // Per-planet transit dignity multiplier — scales that planet's contribution up/down
    const tDig = dignityMultiplier(transitDignityMap[planet as PlanetName], 'transit');
    
    // Jupiter in/tri to target → +12 (scaled by transit dignity)
    if (!jupiterTrineBonus && planet === 'Jupiter') {
      if (isTrineToAny(house)) {
        P += 12 * tDig;
        jupiterTrineBonus = true;
      }
    }
    
    // Saturn in/aspecting target → -12 (debilitated Saturn = less severe; exalted = more severe)
    if (!saturnPenalty && planet === 'Saturn') {
      const satAspects = getSaturnAspects(house);
      const hitsTarget = [house, ...satAspects].some(h => targetSet.has(h));
      if (hitsTarget) {
        // For malefic impact, invert multiplier direction: exalted Saturn hurts more
        const satMult = tDig >= 1 ? tDig : (2 - tDig); // exalted→amplify, debil→soften
        P -= 12 * satMult;
        saturnPenalty = true;
      }
    }
    
    // Mars/Rahu/Ketu in 1/6/8/12 for risk areas → -10
    if (!marRahKetRisk && (planet === 'Mars' || planet === 'Rahu' || planet === 'Ketu')) {
      const riskHouses: HouseNum[] = [1, 6, 8, 12];
      if (['Health', 'AccidentRisk', 'Self', 'Travel'].includes(lifeArea) && riskHouses.includes(house)) {
        const marsMult = tDig >= 1 ? tDig : (2 - tDig);
        P -= 10 * marsMult;
        marRahKetRisk = true;
      }
    }
    
    // Benefic in 2/5/9/10/11 for wealth/career/investments/children/travel → +8
    if (!beneficWealth && (planet === 'Jupiter' || planet === 'Venus')) {
      const wealthHouses: HouseNum[] = [2, 5, 9, 10, 11];
      const wealthAreas = ['Wealth', 'Career', 'Investments', 'Children', 'Travel'];
      if (wealthAreas.includes(lifeArea) && wealthHouses.includes(house)) {
        P += 8 * tDig;
        beneficWealth = true;
      }
    }
    
    // Transit over SD planet's natal occupied house → +10
    // Transit over PD planet's natal occupied house → +6
    if (transitActivation < 16) { // cap +16
      if (sdOccupiedHouse && house === sdOccupiedHouse) {
        P += 10 * tDig;
        transitActivation += 10;
      } else if (pdOccupiedHouse && house === pdOccupiedHouse && transitActivation < 10) {
        P += 6 * tDig;
        transitActivation += 6;
      }
    }
    
    // SD planet transiting its own natal ruled house → +4 per hit (cap +8)
    if (sdRuledTransit < 8 && planet === sdPlanet && sdRuledHouses.includes(house)) {
      P += 4 * tDig;
      sdRuledTransit += 4;
    }
  }
  
  // Hard node hits (Rahu/Ketu) to target house → -8
  const nodeHits = transits.positions.filter(p => 
    (p.planet === 'Rahu' || p.planet === 'Ketu') && targetSet.has(p.house)
  );
  if (nodeHits.length > 0) {
    P -= 8;
  }
  
  // Combustion multiplier
  if (isCombust) {
    P = P * 0.8;
  }
  
  // Cap total |P| at 40
  P = Math.max(-40, Math.min(40, P));
  
  return P;
}

/**
 * Generate events following the complete gate system
 */
export interface GenerateFeedsOptions {
  dasha: DashaContext;
  transits: TransitContext;
  functional: FunctionalClassification;
  combustionMap?: Partial<Record<PlanetName, boolean>>;
  /** D1 natal dignity of each planet — used to scale computeBaseImpact for SD/PD levels */
  natalDignityMap?: Partial<Record<PlanetName, DignityStatus>>;
  /** Current transit dignity of each planet — used to scale Gate D transit contributions */
  transitDignityMap?: Partial<Record<PlanetName, DignityStatus>>;
}

export function generateFeeds(options: GenerateFeedsOptions): FeedItem[] {
  const { dasha, transits, functional, combustionMap = {}, natalDignityMap = {}, transitDignityMap = {} } = options;
  const items: FeedItem[] = [];
  
  // Extract PD and SD levels
  const sdLevel = dasha.levels.find(l => l.level === 'SD');
  const pdLevel = dasha.levels.find(l => l.level === 'PD');
  
  if (!sdLevel && !pdLevel) {
    // No event-firing levels active
    return [];
  }
  
  // Map of life area → accumulated data
  const areaMap = new Map<LifeArea, {
    totalScore: number;
    evidence: string[];
    houses: Set<HouseNum>;
    primaryLevel: 'SD' | 'PD';
    window: { from: string; to: string };
  }>();
  
  // Process SD level first (highest priority)
  if (sdLevel) {
    const sdHouses = getActiveHouses(sdLevel);
    const sdAreas = housesToLifeAreas(sdHouses);
    
    if (sdAreas.length > 0) {
      for (const area of sdAreas) {
        const relevantHouses = sdHouses.filter(h => 
          (HOUSE_TO_LIFE[h] || []).includes(area)
        );
        
        const rawBaseImpact = computeBaseImpact(
          relevantHouses,
          'SD',
          sdLevel.planet,
          dasha.houseScores,
          functional,
          dasha.natalPlacements
        );
        // Apply natal dignity multiplier to the dasha lord's base impact
        const natalMult = dignityMultiplier(natalDignityMap[sdLevel.planet], 'natal');
        const baseImpact = rawBaseImpact * natalMult;
        
        const transitP = computeTransitReinforcement({
          targetHouses: relevantHouses,
          transits,
          lifeArea: area,
          sdPlanet: sdLevel.planet,
          sdOccupiedHouse: sdLevel.occupies,
          sdRuledHouses: sdLevel.rules,
          isCombust: (combustionMap && combustionMap[sdLevel.planet]) || false,
          transitDignityMap: transitDignityMap as Partial<Record<PlanetName, DignityStatus>>
        });
        
        const score = Math.max(-100, Math.min(100, baseImpact + transitP));
        
        const evidence: string[] = [];
        evidence.push(`SD ${sdLevel.planet} rules [${sdLevel.rules.join(',')}] occ H${sdLevel.occupies} asp [${(sdLevel.aspects||[]).join(',')}] natalDig=${natalDignityMap[sdLevel.planet]??'neutral'}(×${dignityMultiplier(natalDignityMap[sdLevel.planet],'natal').toFixed(2)})`);  
        evidence.push(`Target houses: [${relevantHouses.join(',')}]`);
        evidence.push(`BaseImpact=${baseImpact.toFixed(1)} TransitP=${transitP.toFixed(1)}`);
        relevantHouses.forEach(h => {
          const hs = dasha.houseScores[h] ?? 0;
          evidence.push(`H${h} score=${hs}`);
        });
        
        areaMap.set(area, {
          totalScore: score,
          evidence,
          houses: new Set(relevantHouses),
          primaryLevel: 'SD',
          window: { from: sdLevel.from, to: sdLevel.to }
        });
      }
    }
  }
  
  // Process PD level
  if (pdLevel) {
    const pdHouses = getActiveHouses(pdLevel);
    const pdAreas = housesToLifeAreas(pdHouses);
    
    for (const area of pdAreas) {
      const relevantHouses = pdHouses.filter(h => 
        (HOUSE_TO_LIFE[h] || []).includes(area)
      );
      
      const rawPdBaseImpact = computeBaseImpact(
        relevantHouses,
        'PD',
        pdLevel.planet,
        dasha.houseScores,
        functional,
        dasha.natalPlacements
      );
      // Apply natal dignity multiplier to PD lord's base impact
      const pdNatalMult = dignityMultiplier(natalDignityMap[pdLevel.planet], 'natal');
      const baseImpact = rawPdBaseImpact * pdNatalMult;
      
      const transitP = computeTransitReinforcement({
        targetHouses: relevantHouses,
        transits,
        lifeArea: area,
        pdPlanet: pdLevel.planet,
        pdOccupiedHouse: pdLevel.occupies,
        pdRuledHouses: pdLevel.rules,
        sdPlanet: sdLevel?.planet,
        sdOccupiedHouse: sdLevel?.occupies,
        isCombust: (combustionMap && combustionMap[pdLevel.planet]) || false,
        transitDignityMap: transitDignityMap as Partial<Record<PlanetName, DignityStatus>>
      });
      
      const score = Math.max(-100, Math.min(100, baseImpact + transitP));
      
      const evidence: string[] = [];
      evidence.push(`PD ${pdLevel.planet} rules [${pdLevel.rules.join(',')}] occ H${pdLevel.occupies} asp [${(pdLevel.aspects||[]).join(',')}] natalDig=${natalDignityMap[pdLevel.planet]??'neutral'}(×${dignityMultiplier(natalDignityMap[pdLevel.planet],'natal').toFixed(2)})`);  
      evidence.push(`Target houses: [${relevantHouses.join(',')}]`);
      evidence.push(`BaseImpact=${baseImpact.toFixed(1)} TransitP=${transitP.toFixed(1)}`);
      relevantHouses.forEach(h => {
        const hs = dasha.houseScores[h] ?? 0;
        evidence.push(`H${h} score=${hs}`);
      });
      
      const existing = areaMap.get(area);
      if (existing) {
        // SD already processed this area; add PD contribution
        existing.totalScore += score;
        existing.evidence.push(...evidence);
        relevantHouses.forEach(h => existing.houses.add(h));
      } else {
        areaMap.set(area, {
          totalScore: score,
          evidence,
          houses: new Set(relevantHouses),
          primaryLevel: 'PD',
          window: { from: pdLevel.from, to: pdLevel.to }
        });
      }
    }
  }
  
  // Gate E: Threshold - Fire events only if |Score| >= 30
  // Soft feeds: 20 ≤ |Score| < 30 only if SD and at least one transit bonus
  for (const [area, data] of areaMap.entries()) {
    const absScore = Math.abs(data.totalScore);
    
    // Hard threshold
    if (absScore >= 30) {
      items.push(createFeedItem(area, data, dasha));
    }
    // Soft feed (only for SD with transit support)
    else if (absScore >= 20 && absScore < 30 && data.primaryLevel === 'SD') {
      // Check if there was any transit bonus (look for TransitP != 0 in evidence)
      const hasTransitSupport = data.evidence.some(e => 
        e.includes('TransitP=') && !e.includes('TransitP=0.0')
      );
      if (hasTransitSupport) {
        items.push(createFeedItem(area, data, dasha));
      }
    }
  }
  
  // === INTEGRATE RULES SYSTEM ===
  // Merge classical yogas from database (if preloaded) with hardcoded rules
  const classicalYogaRules = getLastLoadedYogaRules();
  const allRules = [...RULES, ...classicalYogaRules];
  
  for (const rule of allRules) {
    try {
      const triggered = rule.triggers && rule.triggers(dasha, transits);
      if (!triggered) continue;
      
      const scoreResult = rule.score(dasha, transits);
      const { value, notes, customWindow, templateKey: dynamicTemplateKey } = scoreResult;
      const severity = (rule as any).severity_override || getSeverity(value);
      
      // Use custom window if provided, otherwise use dasha window
      const windowToUse = customWindow || dasha.window;
      
      // Use dynamic template key from score result if available, otherwise use rule's templateKey
      const finalTemplateKey = dynamicTemplateKey || rule.templateKey;
      
      // Use custom prediction if available, otherwise use template
      const templateFn = TEMPLATES[finalTemplateKey as keyof typeof TEMPLATES] as undefined | ((ctx:{window:{from:string;to:string}})=>string);
      
      // Short message for notification cards — resolved from SHORT_TEMPLATES using finalTemplateKey
      const shortRuleFn = SHORT_TEMPLATES[finalTemplateKey as keyof typeof SHORT_TEMPLATES];
      const ruleShortMessage = shortRuleFn ? shortRuleFn({ window: windowToUse }) : undefined;
      
      // For neech_bhang rules, use first note as title to show planet name
      let message: string;
      if (rule.key && rule.key.startsWith('neech_bhang_') && notes && notes.length > 0) {
        message = notes[0]; // First note contains planet name in title
      } else {
        message = rule.customPrediction 
          ? rule.customPrediction 
          : (templateFn ? templateFn({ window: windowToUse }) : `${rule.key} active`);
      }
      
      // Check if Rahu is in any dasha level
      const hasRahuInDasha = dasha.levels.some(level => level.planet === 'Rahu');
      
      // For malefic_dusthana_from_own_house rule, compute areas dynamically from afflicted houses
      let areasToUse = rule.areas;
      if (rule.key === 'malefic_dusthana_from_own_house' && dasha.natalPlacements) {
        const afflictedHouses = new Set<number>();
        
        // Parse afflicted houses from notes
        const afflictionNote = notes.find((n: string) => n.startsWith('NATAL AFFLICTION:'));
        if (afflictionNote) {
          // Extract house numbers like "H5", "H3" from the note
          const houseMatches = afflictionNote.matchAll(/H(\d+)/g);
          for (const match of houseMatches) {
            afflictedHouses.add(parseInt(match[1]));
          }
        }
        
        // Convert afflicted houses to life areas
        if (afflictedHouses.size > 0) {
          const dynamicAreas = new Set<any>();
          const HOUSE_TO_LIFE_LOCAL: any = {
            1: ['Self','Health'], 2: ['Wealth'], 3: ['Travel'], 4: ['Mother','Property'],
            5: ['Children'], 6: ['Health'], 7: ['Spouse'], 8: ['Health'],
            9: ['Father','Spirituality'], 10: ['Career','Property'], 11: ['Wealth','Career'], 12: ['Spirituality','Health']
          };
          
          afflictedHouses.forEach(h => {
            const areas = HOUSE_TO_LIFE_LOCAL[h] || [];
            areas.forEach((a: any) => dynamicAreas.add(a));
          });
          
          if (dynamicAreas.size > 0) {
            areasToUse = Array.from(dynamicAreas) as any[];
          }
        }
      }
      
      for (const area of areasToUse) {
        const id = `rule_${rule.key}_${area}_${windowToUse.from}`;
        const feedItem = {
          id,
          lifeArea: area,
          severity: severity as any,
          score: value,
          message,
          shortMessage: ruleShortMessage,
          window: windowToUse,  // Use custom window if available
          evidence: notes,
          highProbability: hasRahuInDasha,
          ruleKey: rule.key,
          private: rule.private || false  // Propagate private flag from rule
        };
        
        items.push(feedItem);
      }
    } catch (err) {
      console.warn('[generateFeeds] Rule failed:', rule.key, err);
    }
  }
  
  // Sort by absolute score (highest first)
  items.sort((a, b) => Math.abs(b.score) - Math.abs(a.score));
  
  // Dedupe: max 1 per life area per window
  const dedupedItems = dedupeByArea(items);
  
  // Return all items (don't cap - let UI handle filtering)
  return dedupedItems;
}

function createFeedItem(
  area: LifeArea,
  data: { totalScore: number; evidence: string[]; window: { from: string; to: string } },
  dasha: DashaContext,
  ruleKey?: string
): FeedItem {
  const score = data.totalScore;
  const severity = getSeverity(score);
  
  // Check if Rahu is in any dasha level (MD/AD/PD/SD) for high probability flag
  const hasRahuInDasha = dasha.levels.some(level => level.planet === 'Rahu');
  
  // Pick template based on area and sign
  const templateKey = selectTemplate(area, score);
  
  // If template doesn't exist, try without suffix, or use generic fallback
  let template = TEMPLATES[templateKey as keyof typeof TEMPLATES] as undefined | ((ctx:{window:{from:string;to:string}})=>string);
  if (!template) {
    // Try base template without _positive/_alert suffix
    const baseParts = templateKey.split('_');
    const baseSuffix = baseParts[baseParts.length - 1];
    if (baseSuffix === 'positive' || baseSuffix === 'alert') {
      const baseKey = baseParts.slice(0, -1).join('_');
      template = TEMPLATES[baseKey as keyof typeof TEMPLATES];
    }
  }
  
  const message = template 
    ? template({ window: data.window }) 
    : `${area} area ${score >= 0 ? 'favorable' : 'challenging'} between ${data.window.from} and ${data.window.to}`;

  // Short message for notification cards — use SHORT_TEMPLATES if available
  const shortFn = SHORT_TEMPLATES[templateKey as keyof typeof SHORT_TEMPLATES];
  const shortMessage = shortFn ? shortFn({ window: data.window }) : undefined;
  
  // Generate stable ID
  const id = generateId(area, data.window, score);
  
  return {
    id,
    lifeArea: area,
    severity,
    score: Math.round(score),
    message,
    shortMessage,
    window: data.window,
    evidence: data.evidence,
    highProbability: hasRahuInDasha,
    ruleKey
  };
}

/**
 * Severity buckets per specification
 */
function getSeverity(score: number): 'positive'|'mixed'|'caution'|'alert' {
  if (score >= 30) return 'positive';
  if (score >= 10) return 'mixed'; // positive tilt
  if (score > -10) return 'mixed'; // should not fire, but safety
  if (score > -30) return 'mixed'; // caution tilt
  if (score > -50) return 'caution';
  return 'alert';
}

/**
 * Select appropriate template based on life area and score sign
 */
function selectTemplate(area: LifeArea, score: number): string {
  const isPositive = score >= 0;
  
  // Map area to template key
  const areaKeyMap: Record<LifeArea, string> = {
    'Mother': 'mother_health',
    'Father': 'father_guidance',
    'Spouse': 'spouse_boundaries',
    'Children': 'children_blessing',
    'Career': 'promotion_opportunity',
    'Wealth': 'wealth_gains',
    'Investments': 'investment_caution',
    'Property': 'property_gains',
    'Health': 'health_hygiene',
    'AccidentRisk': 'accident_risk',
    'Travel': 'travel_gains',
    'Spirituality': 'spiritual_growth',
    'Litigation': 'litigation_caution',
    'Self': 'self_development'
  };
  
  const base = areaKeyMap[area] || 'generic';
  return isPositive ? `${base}_positive` : `${base}_alert`;
}

function generateId(area: LifeArea, window: { from: string; to: string }, score: number): string {
  const str = `${area}|${window.from}|${window.to}|${Math.round(score / 5) * 5}`;
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return ('00000000' + h.toString(16)).slice(-8);
}

function dedupeByArea(items: FeedItem[]): FeedItem[] {
  // Separate yogas from regular feeds - yogas should NEVER be deduplicated
  const yogas: FeedItem[] = [];
  const regularFeeds: FeedItem[] = [];
  
  for (const item of items) {
    // Yogas and Doshas should NEVER be deduplicated
    // This includes: yogas, doshas, afflictions, Kaal Sarp, Sade Sati, conjunctions, oppositions
    const isYogaOrDosha = item.ruleKey?.includes('yoga') || 
                          item.ruleKey?.includes('dosha') ||
                          item.ruleKey?.includes('affliction') ||
                          item.ruleKey?.includes('sarp') || 
                          item.ruleKey?.startsWith('sade_sati') ||
                          item.ruleKey?.includes('conjunction') ||
                          item.ruleKey?.includes('opposition');
    if (isYogaOrDosha) {
      yogas.push(item);
    } else {
      regularFeeds.push(item);
    }
  }
  
  // Dedupe regular feeds: keep best score per life area
  const best = new Map<LifeArea, FeedItem>();
  for (const item of regularFeeds) {
    const existing = best.get(item.lifeArea);
    if (!existing || Math.abs(item.score) > Math.abs(existing.score)) {
      best.set(item.lifeArea, item);
    }
  }
  
  // Return all yogas + deduplicated regular feeds
  return [...yogas, ...Array.from(best.values())];
}
